/**
 * The hub connection (realtime-service, design/07 §9.1, ADR-0010): **one** SSE connection per tab
 * carrying every hub topic the page subscribed to.
 *
 * - Transport: fetch + `ReadableStream` (sends `Authorization` — `EventSource` cannot), or — for
 *   guests only (no token provider) with `transport: "auto"` — the browser's `EventSource`
 *   (native reconnect with `Last-Event-ID`, and test tools treat it as a stream).
 * - Topics change on the open stream through `POST /subscriptions {connectionId, add, remove}` —
 *   no reconnect; when that fails the stream is reopened with the whole list. No topic left →
 *   the stream closes after `idleCloseMs` (the next page usually brings topics before that).
 * - Reconnect 1 → 2 → 4 … 30 s, resuming with `Last-Event-ID` (the hub replays 10 minutes, else
 *   `resync`); `Retry-After` of a 429/503 is respected. After 3 failures in a row every topic
 *   reports `fallback` (the client polls) while reconnecting continues in the background.
 * - 403 names the refused topics (`details[{topic}]`) → those report `fallback`, the rest stay live.
 * - `error {code:"unauthorized"}` / 401 → a fresh token (force refresh) and reconnect; without a
 *   token provider the refused `order:` topics fall back. `superseded` (a 6th tab of the same
 *   person) → fallback until the tab is visible again.
 * - A stream that sends nothing (not even the 20 s `: ping`) for `stallMs` is treated as dead.
 * - The subscribe gap (T13-40 review, 2026-10-04): a page reads its data first and subscribes
 *   after — a change in between was lost (a product page opened 0.3 s before a hold saw it only
 *   through the 15 s poll). A topic the hub confirms for the first time (`ready` or the
 *   `POST /subscriptions` answer) is resynced once (`reason: "subscribed"`): the page re-reads,
 *   and later changes arrive on the stream. A resume by `Last-Event-ID` covers topics that were
 *   already confirmed (the hub replays them).
 */
import {
  deniedTopics,
  discard,
  parseChange,
  parseControl,
  retryAfterMs,
  runFetchStream,
  type SseEvent,
} from "./sse";
import { isHubTopic } from "./topics";
import {
  type ConnectionStatus,
  retryDelay,
  type RetryOptions,
  type Source,
  type SourceHost,
  type TopicStatus,
} from "./types";

export interface HubOptions extends RetryOptions {
  /** Gateway prefix of the hub. Default `/api/v1/realtime`. */
  baseUrl?: string;
  /** No topic left → close after this long. Default 5 s. */
  idleCloseMs?: number;
  /** Hub limit per connection (`REALTIME_MAX_TOPICS_PER_CONNECTION`). Default 200. */
  maxTopics?: number;
  /**
   * `auto` (default): `EventSource` for guests (no token provider), fetch otherwise; `fetch`
   * always uses fetch.
   */
  transport?: "auto" | "fetch";
  /**
   * A tab closed as `superseded` (the 6th of the same person) tries again after this long even
   * while it stays visible (a second monitor) — focus / showing the tab again retries at once.
   * Default 5 min.
   */
  supersededRetryMs?: number;
}

interface Conn {
  controller: AbortController;
  connectionId: string | null;
  /** Topics the hub confirmed for this connection. */
  wire: Set<string>;
  posting: boolean;
  source: EventSource | null;
  gotReady: boolean;
}

const ORDER_PREFIX = "order:";
/** The digital receipt page (T14-16): proven with the page's link token, sent as `receiptToken`. */
const RECEIPT_PREFIX = "receipt:";
/** Topics proven by the page's link token (the client's `orderToken` slot). */
const isLinkTopic = (t: string) => t.startsWith(ORDER_PREFIX) || t.startsWith(RECEIPT_PREFIX);

export function createHubSource(host: SourceHost, options: HubOptions = {}): Source {
  const base = (options.baseUrl ?? "/api/v1/realtime").replace(/\/+$/, "");
  const idleCloseMs = options.idleCloseMs ?? 5_000;
  const batchMs = options.batchMs ?? 50;
  const minRetryMs = options.minRetryMs ?? 1_000;
  const maxRetryMs = options.maxRetryMs ?? 30_000;
  const fallbackAfter = options.fallbackAfter ?? 3;
  const stallMs = options.stallMs ?? 45_000;
  const maxTopics = options.maxTopics ?? 200;
  const transport = options.transport ?? "auto";
  const supersededRetryMs = options.supersededRetryMs ?? 300_000;

  const topics = new Set<string>();
  const denied = new Set<string>();
  let status: ConnectionStatus = "idle";
  let conn: Conn | null = null;
  let paused: "hidden" | "offline" | "pagehide" | null = null;
  let superseded = false;
  let closed = false;
  let failures = 0;
  let lastEventId: string | null = null;
  let everLive = false;
  let resyncOnReady = false;
  /** Topics the hub confirmed at least once since they were added (see the subscribe gap). */
  const confirmed = new Set<string>();
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let batchTimer: ReturnType<typeof setTimeout> | null = null;

  const clear = (timer: ReturnType<typeof setTimeout> | null) => {
    if (timer !== null) {
      clearTimeout(timer);
    }
    return null;
  };

  function setStatus(next: ConnectionStatus): void {
    status = next;
    host.statusChanged();
  }

  function topicStatus(topic: string): TopicStatus {
    if (denied.has(topic) || status === "fallback") {
      return "fallback";
    }
    return status === "live" && conn?.wire.has(topic) ? "live" : "connecting";
  }

  /** Topics that should be on the wire now (hub limit: the first `maxTopics`). */
  function wanted(): string[] {
    return [...topics].filter((t) => !denied.has(t)).slice(0, maxTopics);
  }

  function schedule(delay = batchMs): void {
    if (batchTimer === null && !closed) {
      batchTimer = setTimeout(() => {
        batchTimer = null;
        reconcile();
      }, delay);
    }
  }

  function drop(): void {
    if (conn) {
      conn.controller.abort();
      conn.source?.close();
      conn = null;
    }
  }

  function reconcile(): void {
    if (closed || paused !== null || superseded || retryTimer !== null) {
      return;
    }
    const want = wanted();
    if (want.length === 0) {
      if (conn && idleTimer === null) {
        idleTimer = setTimeout(() => {
          idleTimer = null;
          if (wanted().length === 0) {
            drop();
            setStatus("idle");
          }
        }, idleCloseMs);
      } else if (!conn && status !== "idle") {
        setStatus("idle");
      }
      return;
    }
    idleTimer = clear(idleTimer);
    if (!conn) {
      connect(want);
      return;
    }
    if (!conn.connectionId || conn.posting) {
      return; // after `ready` / the running POST this runs again
    }
    const current = conn;
    const add = want.filter((t) => !current.wire.has(t));
    const remove = [...current.wire].filter((t) => !want.includes(t));
    if (add.length > 0 || remove.length > 0) {
      void post(current, add, remove);
    }
  }

  function withOrderToken(list: readonly string[]): string | null {
    const token = host.orderToken();
    return token !== null && list.some((t) => t.startsWith(ORDER_PREFIX)) ? token : null;
  }

  /** The same link token proves `receipt:<no>` (the receipt QR's `t`) — sent as `receiptToken`. */
  function withReceiptToken(list: readonly string[]): string | null {
    const token = host.orderToken();
    return token !== null && list.some((t) => t.startsWith(RECEIPT_PREFIX)) ? token : null;
  }

  function fail(waitAtLeast = 0): void {
    drop();
    failures += 1;
    setStatus(failures >= fallbackAfter ? "fallback" : "reconnecting");
    retryTimer = setTimeout(
      () => {
        retryTimer = null;
        reconcile();
      },
      Math.max(waitAtLeast, retryDelay(failures, minRetryMs, maxRetryMs)),
    );
  }

  /** The stream ended without an error (hub restart, proxy limit): reopen soon, resume by id. */
  function reopen(): void {
    drop();
    setStatus(everLive ? "reconnecting" : "connecting");
    retryTimer = setTimeout(() => {
      retryTimer = null;
      reconcile();
    }, minRetryMs / 2);
  }

  /** Topics on the wire for the first time → remember them; the caller resyncs them. */
  function firstConfirmed(wire: Iterable<string>): string[] {
    const fresh = [...wire].filter((t) => topics.has(t) && !confirmed.has(t));
    fresh.forEach((t) => confirmed.add(t));
    return fresh;
  }

  function refuse(list: readonly string[]): void {
    list.forEach((t) => denied.add(t));
    host.statusChanged();
  }

  /** One SSE event of the open stream (both transports). `true` = the stream is finished. */
  function handle(current: Conn, want: readonly string[], event: SseEvent): boolean {
    if (event.event === "change") {
      if (event.id) {
        lastEventId = event.id;
      }
      const message = parseChange(event.data);
      if (message) {
        host.change({ ...message, source: "hub" });
      }
    } else if (event.event === "ready") {
      current.gotReady = true;
      const data = parseControl(event.data);
      current.connectionId = typeof data.connectionId === "string" ? data.connectionId : null;
      current.wire = new Set(
        Array.isArray(data.topics)
          ? data.topics.filter((t): t is string => typeof t === "string")
          : want,
      );
      const resumed = everLive;
      failures = 0;
      everLive = true;
      setStatus("live");
      const fresh = firstConfirmed(current.wire);
      if (resyncOnReady || (resumed && !lastEventId)) {
        // back from a pause / offline, or a reconnect with nothing to resume from
        const reason = resyncOnReady ? "resume" : "hub";
        resyncOnReady = false;
        host.resync(null, reason);
      } else if (fresh.length > 0) {
        host.resync(fresh, "subscribed"); // read before the subscribe: re-read once
      }
      reconcile(); // topics that changed while connecting
    } else if (event.event === "resync") {
      host.resync(null, "hub");
    } else if (event.event === "error") {
      const code = parseControl(event.data).code;
      if (code === "superseded") {
        superseded = true;
        drop();
        setStatus("fallback");
        retryTimer = clear(retryTimer);
        retryTimer = setTimeout(() => {
          retryTimer = null;
          superseded = false;
          resyncOnReady = true;
          reconcile();
        }, supersededRetryMs);
        return true;
      }
      if (code === "unauthorized") {
        if (host.hasTokenProvider()) {
          host.refuseToken();
        } else {
          refuse([...current.wire].filter(isLinkTopic));
        }
        reopen();
        return true;
      }
    }
    return false;
  }

  function streamUrl(want: readonly string[], idInQuery: boolean): string {
    const params = new URLSearchParams();
    params.set("topics", want.join(","));
    const token = withOrderToken(want);
    if (token) {
      params.set("orderToken", token);
    }
    const receiptToken = withReceiptToken(want);
    if (receiptToken) {
      params.set("receiptToken", receiptToken);
    }
    if (idInQuery && lastEventId) {
      params.set("lastEventId", lastEventId); // = Last-Event-ID (EventSource cannot set headers)
    }
    return `${base}/stream?${params.toString()}`;
  }

  function newConn(): Conn {
    return {
      controller: new AbortController(),
      connectionId: null,
      wire: new Set(),
      posting: false,
      source: null,
      gotReady: false,
    };
  }

  /** A refused stream: 403 names the topics → open again without them; else back off. */
  async function refused(current: Conn, response: Response, want: string[]): Promise<void> {
    if (response.status === 403) {
      const list = await deniedTopics(response);
      if (list.length > 0) {
        refuse(list);
        if (conn === current) {
          drop();
        }
        reconcile();
        return;
      }
    } else {
      discard(response);
    }
    if (response.status === 401 && host.hasTokenProvider()) {
      host.refuseToken();
    } else if (
      (response.status === 401 || response.status === 403) &&
      (withOrderToken(want) !== null || withReceiptToken(want) !== null)
    ) {
      refuse(want.filter(isLinkTopic));
    }
    if (conn === current) {
      fail(retryAfterMs(response));
    }
  }

  function connect(want: string[]): void {
    const current = newConn();
    conn = current;
    setStatus(
      failures >= fallbackAfter
        ? "fallback"
        : everLive || failures > 0
          ? "reconnecting"
          : "connecting",
    );
    if (transport === "auto" && !host.hasTokenProvider() && typeof EventSource === "function") {
      connectEventSource(current, want);
    } else {
      void connectFetch(current, want);
    }
  }

  function connectEventSource(current: Conn, want: string[]): void {
    const url = streamUrl(want, true);
    const source = new EventSource(url);
    current.source = source;
    for (const name of ["ready", "change", "resync"]) {
      source.addEventListener(name, (event) => {
        if (conn === current && event instanceof MessageEvent) {
          handle(current, want, {
            event: name,
            data: typeof event.data === "string" ? event.data : "",
            id: event.lastEventId || null,
          });
        }
      });
    }
    source.addEventListener("error", (event) => {
      if (conn !== current) {
        return;
      }
      if (event instanceof MessageEvent) {
        handle(current, want, { event: "error", data: String(event.data), id: null });
        return;
      }
      if (source.readyState === EventSource.CONNECTING) {
        // the browser reconnects by itself (network blip, stream ended) — count it
        current.gotReady = false;
        failures += 1;
        setStatus(failures >= fallbackAfter ? "fallback" : "reconnecting");
        return;
      }
      // CLOSED: an HTTP error — EventSource cannot say which; ask once with fetch when an order
      // link is in the list (was the link refused?)
      if (withOrderToken(want) !== null || withReceiptToken(want) !== null) {
        void probe(current, url, want);
      } else {
        fail();
      }
    });
  }

  async function probe(current: Conn, url: string, want: string[]): Promise<void> {
    const controller = new AbortController();
    try {
      const response = await host.fetch(url, {
        headers: { Accept: "text/event-stream" },
        signal: controller.signal,
        cache: "no-store",
        credentials: "same-origin",
      });
      if (response.ok) {
        controller.abort();
        if (conn === current) {
          fail();
        }
        return;
      }
      await refused(current, response, want);
    } catch {
      if (conn === current) {
        fail();
      }
    }
  }

  async function connectFetch(current: Conn, want: string[]): Promise<void> {
    const bearer = await host.token();
    if (conn !== current) {
      return;
    }
    await runFetchStream({
      fetch: host.fetch,
      url: streamUrl(want, false),
      bearer,
      lastEventId,
      controller: current.controller,
      stallMs,
      onRefused: (response) => refused(current, response, want),
      onEvent: (event) => handle(current, want, event),
      onFail: () => {
        fail();
      },
      onEnd: () => {
        if (current.gotReady) {
          reopen();
        } else {
          fail();
        }
      },
    });
  }

  async function post(current: Conn, add: string[], remove: string[]): Promise<boolean> {
    current.posting = true;
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    const bearer = await host.token();
    if (bearer) {
      headers.Authorization = `Bearer ${bearer}`;
    }
    const token = withOrderToken(add);
    const receiptToken = withReceiptToken(add);
    let response: Response | null;
    try {
      response = await host.fetch(`${base}/subscriptions`, {
        method: "POST",
        headers,
        cache: "no-store",
        credentials: "same-origin",
        body: JSON.stringify({
          connectionId: current.connectionId,
          add,
          remove,
          ...(token ? { orderToken: token } : {}),
          ...(receiptToken ? { receiptToken } : {}),
        }),
      });
    } catch {
      response = null;
    }
    current.posting = false;
    if (conn !== current) {
      return false;
    }
    const expected = () =>
      new Set([...[...current.wire].filter((t) => !remove.includes(t)), ...add]);
    if (response?.ok) {
      try {
        const body = (await response.json()) as { topics?: unknown };
        current.wire = Array.isArray(body.topics)
          ? new Set(body.topics.filter((t): t is string => typeof t === "string"))
          : expected();
      } catch {
        current.wire = expected();
      }
      host.statusChanged();
      const fresh = firstConfirmed(current.wire);
      if (fresh.length > 0) {
        host.resync(fresh, "subscribed");
      }
      reconcile();
      return true;
    }
    if (response?.status === 403) {
      const list = await deniedTopics(response);
      if (list.length > 0) {
        refuse(list);
        reconcile();
        return false;
      }
    } else if (response) {
      discard(response);
    }
    if (response?.status === 401 && host.hasTokenProvider()) {
      host.refuseToken();
    }
    // unknown connection (hub restarted), network … → open again with the whole list
    drop();
    reconcile();
    return false;
  }

  function stop(): void {
    retryTimer = clear(retryTimer);
    idleTimer = clear(idleTimer);
    batchTimer = clear(batchTimer);
    drop();
  }

  return {
    name: "hub",
    accepts: isHubTopic,
    add(topic) {
      topics.add(topic);
      schedule();
    },
    remove(topic) {
      if (topics.delete(topic)) {
        confirmed.delete(topic); // added again later = read again → resync again
        if (isLinkTopic(topic) || topic.startsWith("consignor:")) {
          denied.delete(topic); // a later page may prove it again
        }
        schedule();
      }
    },
    status: () => status,
    topicStatus,
    pause(reason) {
      paused = reason;
      stop();
      setStatus(reason === "offline" ? "offline" : "paused");
    },
    resume() {
      if (paused === null && !superseded) {
        return;
      }
      if (paused === "offline") {
        failures = 0;
      }
      paused = null;
      superseded = false;
      retryTimer = clear(retryTimer);
      resyncOnReady = true;
      reconcile();
    },
    tokenChanged() {
      // renew on the open stream (T14-05): the hub adopts the new token's expiry
      if (conn?.connectionId && !conn.posting) {
        void post(conn, [], []);
      }
    },
    orderTokenChanged() {
      for (const topic of [...denied]) {
        if (isLinkTopic(topic)) {
          denied.delete(topic);
        }
      }
      schedule();
    },
    wireTopics: () => [...(conn?.wire ?? [])],
    close() {
      closed = true;
      stop();
      topics.clear();
      confirmed.clear();
      status = "idle";
    },
  };
}
